import React, { useEffect, useState } from 'react';

/**
 * "נשאר על המסך בגלילה" — sticky blocks.
 *
 * A block marked `props.sticky` keeps its authored place in the canvas; once
 * the reader scrolls past it, a pinned copy appears at the top/bottom edge so
 * the meter / call-to-action is always reachable. Cloning (instead of just
 * pinning the original) means the layout never gets a hole where the block was.
 *
 * Shared by the public page (fixed to the viewport) and the builder preview
 * (absolute inside the device frame) — same markup, different anchor.
 */

export const STICKY_TYPES = ['progress', 'button', 'payment-button', 'share'] as const;

type AnyBlock = { type: string; props?: any; mobileProps?: any };

/** Is this block set to stay on screen, for the given device? */
export function isStickyBlock(block: AnyBlock, device: 'desktop' | 'mobile'): boolean {
  if (!STICKY_TYPES.includes(block.type as any)) return false;
  const p = device === 'mobile' ? { ...block.props, ...block.mobileProps } : (block.props || {});
  return !!p.sticky;
}

export function stickyPosOf(block: AnyBlock, device: 'desktop' | 'mobile'): 'top' | 'bottom' {
  const p = device === 'mobile' ? { ...block.props, ...block.mobileProps } : (block.props || {});
  return p.stickyPos === 'top' ? 'top' : 'bottom';
}

/**
 * When the pinned copy shows.
 *  - 'scroll' (default): only once the authored block leaves the viewport, so
 *    the reader never sees it twice.
 *  - 'always': pinned from the first paint and never leaves. The authored copy
 *    is then hidden in place (its slot stays empty) — again, never two.
 */
export function stickyModeOf(block: AnyBlock, device: 'desktop' | 'mobile'): 'scroll' | 'always' {
  const p = device === 'mobile' ? { ...block.props, ...block.mobileProps } : (block.props || {});
  return p.stickyMode === 'always' ? 'always' : 'scroll';
}

/** Sticky blocks pinned from the start — their in-flow copy must be hidden. */
export function alwaysPinnedIds(blocks: AnyBlock[], device: 'desktop' | 'mobile'): Set<string> {
  const ids = new Set<string>();
  for (const b of blocks) {
    if (isStickyBlock(b, device) && stickyModeOf(b, device) === 'always' && (b as any).id) {
      ids.add((b as any).id);
    }
  }
  return ids;
}

/** Hide a block where it was authored without disturbing layout or
 *  measurements — used for the in-flow copy of an always-pinned block. */
export function hideInFlow(el: React.ReactElement): React.ReactElement {
  const style = (el.props as any)?.style || {};
  return React.cloneElement(el, {
    'aria-hidden': true,
    style: { ...style, visibility: 'hidden', pointerEvents: 'none' },
  } as any);
}

/** Site chrome that permanently covers a viewport edge — the "מופעל ע״י
 *  Amutech" bar (a sticky top bar on desktop, a FIXED footer under 640px) and
 *  anything else opting in with `data-page-chrome`. */
const CHROME_SELECTOR = '[data-page-chrome], .amutech-topbar, .amutech-bar';

/** Measure, right now, how many px of chrome cover the top and bottom edges. */
export function measureChromeInsets(): { top: number; bottom: number } {
  if (typeof document === 'undefined') return { top: 0, bottom: 0 };
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;
  let top = 0;
  let bottom = 0;
  document.querySelectorAll<HTMLElement>(CHROME_SELECTOR).forEach((el) => {
    const cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return;
    if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
    const r = el.getBoundingClientRect();
    if (r.height <= 0) return;
    // Only count the part actually glued to an edge right now: a sticky top bar
    // that has scrolled away contributes nothing, so no phantom gap is left.
    if (r.top <= 1 && r.bottom > 0) top = Math.max(top, Math.round(r.bottom));
    if (vh && r.bottom >= vh - 1 && r.top < vh) bottom = Math.max(bottom, Math.round(vh - r.top));
  });
  return { top, bottom };
}

/**
 * Live chrome insets. Sticky bars offset by these so a pinned block is never
 * buried under the Amutech bar (the mobile one is fixed at the bottom — the
 * default pin edge — which is exactly where pinned CTAs used to disappear),
 * and the same insets shrink the IntersectionObserver root so a block hidden
 * BEHIND the chrome already counts as off-screen. Without that the reader can
 * briefly see the block twice: half-covered in the page and pinned at once.
 */
export function usePageChromeInsets(): { top: number; bottom: number } {
  const [insets, setInsets] = useState(() => ({ top: 0, bottom: 0 }));
  useEffect(() => {
    const measure = () => {
      const next = measureChromeInsets();
      setInsets(prev => (prev.top === next.top && prev.bottom === next.bottom ? prev : next));
    };
    measure();
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, []);
  return insets;
}

/**
 * The pinned bar. `children` is the block re-rendered with `top: 0` so it sits
 * at the bar's edge; horizontal placement and size stay authored, because the
 * inner wrapper is the same canvas width scaled by the same ratio as the page.
 */
export const StickyBar: React.FC<{
  /** 'fixed' pins to the viewport (public page); 'absolute' to the nearest
   *  positioned ancestor (the preview's device frame). */
  anchor: 'fixed' | 'absolute';
  pos: 'top' | 'bottom';
  canvasWidth: number;
  ratio: number;
  /** Authored block height in canvas px — the bar reserves exactly this. */
  height: number;
  /** Bar background. Defaults to a near-opaque white shelf. */
  background?: string;
  /** px to keep clear of the edge — the site chrome that sits there
   *  (usePageChromeInsets). 0 inside the builder preview, which has none. */
  offset?: number;
  /** Whether the bar should currently be on screen. It stays mounted while
   *  hidden so the exit is animated instead of a hard unmount. */
  shown?: boolean;
  zIndex?: number;
  children: React.ReactNode;
}> = ({
  anchor, pos, canvasWidth, ratio, height, background,
  offset = 0, shown = true, zIndex = 60, children,
}) => {
  // Mount lazily: nothing is rendered until the bar is first needed (so an
  // "after scroll" bar costs nothing on load), and from then on it stays
  // mounted so hiding can animate out rather than vanish.
  const [mounted, setMounted] = useState(shown);
  useEffect(() => { if (shown) setMounted(true); }, [shown]);

  // Respect the reader's motion preference — no slide, just the state change.
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, []);

  // Enter on the frame AFTER mount so the browser has a "hidden" state to
  // transition from — otherwise the first paint is already in place and the
  // slide never runs.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!mounted) return;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [mounted]);

  if (!mounted) return null;

  const visible = shown && entered;
  const away = pos === 'bottom' ? 'translateY(115%)' : 'translateY(-115%)';
  // Gentle overshoot-free easing (Material "emphasized decelerate"): fast out
  // of the edge, soft landing. The block inside lifts a touch as it settles.
  const ease = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  return (
    <div
      data-sticky-bar={pos}
      data-sticky-offset={offset || undefined}
      data-sticky-shown={visible ? '1' : '0'}
      aria-hidden={visible ? undefined : true}
      style={{
        position: anchor,
        left: 0,
        right: 0,
        [pos]: offset,
        zIndex,
        background: background ?? 'rgba(255,255,255,0.96)',
        backdropFilter: 'blur(6px)',
        boxShadow: visible
          ? (pos === 'bottom' ? '0 -4px 20px rgba(0,0,0,0.12)' : '0 4px 20px rgba(0,0,0,0.12)')
          : '0 0 0 rgba(0,0,0,0)',
        // The block is absolutely positioned inside; the bar owns the height.
        height: Math.max(0, height * ratio),
        overflow: 'hidden',
        transform: visible ? 'translateY(0)' : away,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: reduceMotion
          ? 'none'
          : `transform 380ms ${ease}, opacity 240ms ease, box-shadow 380ms ${ease}`,
        willChange: 'transform, opacity',
      } as React.CSSProperties}
    >
      <div
        style={{
          width: canvasWidth,
          height,
          margin: '0 auto',
          position: 'relative',
          zoom: ratio,
          // A hair of scale/lift on the content makes the bar feel like it
          // settles into place instead of snapping.
          transform: visible ? 'scale(1)' : 'scale(0.985)',
          transition: reduceMotion ? 'none' : `transform 420ms ${ease}`,
        } as React.CSSProperties}
      >
        {children}
      </div>
    </div>
  );
};

/**
 * Re-render an already-rendered block for the sticky bar: same element, but
 * pinned to the top of the bar (its authored `top` belongs to the canvas) and
 * with animations stripped so it doesn't replay every time it appears.
 */
export function cloneForStickyBar(el: React.ReactElement, key: string): React.ReactElement {
  const style = (el.props as any)?.style || {};
  const blockId = (el.props as any)?.['data-block-id'];
  return React.cloneElement(el, {
    key,
    // ref:null — the page keys dynamic-height measurements by block id; without
    // this the clone (rendered last) would overwrite the original's ref and
    // drive every sibling's offset from the pinned copy.
    ref: null,
    // The id moves to a separate attribute so the page's own
    // querySelector('[data-block-id=…]') can never resolve to the clone.
    'data-block-id': undefined,
    'data-sticky-clone': blockId,
    // visibility/pointerEvents are reset because the in-flow original of an
    // always-pinned block is hidden (hideInFlow) — the pinned copy must not
    // inherit that.
    style: {
      ...style, top: 0, animation: undefined, transition: undefined,
      visibility: undefined, pointerEvents: undefined,
    },
  } as any);
}
